'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import {
    getSuppliersByOrganization,
    deleteSupplier,
    toggleSupplierActive,
    Supplier,
} from '@/lib/api/suppliers/supplier-api'
import {
    getSupplierTypesByOrganization,
    SupplierType,
} from '@/lib/api/suppliers/supplier-type-api'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdSearch,
    MdClose,
    MdLocalShipping,
    MdPhone,
    MdEmail,
    MdLocationOn,
    MdArrowForward,
    MdAdd,
    MdEdit,
    MdDelete,
    MdBusiness,
    MdToggleOn,
    MdToggleOff,
    MdFilterList,
    MdLanguage,
} from 'react-icons/md'
import PaginationControls from '@/components/pagination/PaginationControls'

const getInitials = (name: string): string => {
    const parts = name.trim().split(' ')
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
    return (
        parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase()
}

const getAvatarColor = (name: string): string => {
    const colors = [
        'from-secondary to-secondary/70',
        'from-secondary to-secondary/70',
        'from-accent to-accent/70',
        'from-info to-info/70',
        'from-success to-success/70',
        'from-warning to-warning/70',
        'from-error to-error/70',
    ]
    const index = name.charCodeAt(0) % colors.length
    return colors[index]
}

const formatCnpj = (cnpj: string): string => {
    const digits = cnpj.replace(/\D/g, '')
    if (digits.length !== 14) return cnpj
    return digits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5'
    )
}

export default function SuppliersPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canDelete } = useResourcePermissions(
        'person',
        'suppliers'
    )
    const [showSearch, setShowSearch] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [deleteModalId, setDeleteModalId] = useState<number | null>(null)
    const [supplierTypes, setSupplierTypes] = useState<SupplierType[]>([])
    const [typeFilter, setTypeFilter] = useState<number | null>(null)
    const [urlPage, setUrlPage] = useUrlPage()

    const debouncedSearch = useDebounce(searchTerm, 300)

    useEffect(() => {
        if (user?.organizationId) {
            getSupplierTypesByOrganization(user.organizationId).then(res => {
                if (res.success) setSupplierTypes(res.result)
            })
        }
    }, [user?.organizationId])

    const filters = useMemo(
        () => ({
            typeId: typeFilter ?? undefined,
            search: debouncedSearch.trim() || undefined,
        }),
        [typeFilter, debouncedSearch]
    )

    const fetchFn = useCallback(
        (params: typeof filters & { page: number; limit: number }) => {
            return getSuppliersByOrganization(user!.organizationId, params)
        },
        [user?.organizationId]
    )

    const {
        items: displayedSuppliers,
        loading,
        page,
        limit,
        total,
        setPage,
        reload,
    } = usePaginatedPage<Supplier, typeof filters>({
        fetchFn,
        filters,
        limit: 15,
        enabled: !!user?.organizationId,
        page: urlPage,
        onPageChange: setUrlPage,
    })

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
    }

    const handleClearSearch = () => {
        setSearchTerm('')
    }

    const handleToggleActive = async (e: React.MouseEvent, id: number) => {
        e.stopPropagation()
        try {
            const response = await toggleSupplierActive(id)
            if (response.success) {
                reload()
                toast.success('Status do fornecedor atualizado')
            } else {
                toast.error(response.error || 'Erro ao alterar status')
            }
        } catch {
            toast.error('Erro ao alterar status')
        }
    }

    const handleDelete = async () => {
        if (!deleteModalId) return
        try {
            const response = await deleteSupplier(deleteModalId)
            if (response.success) {
                reload()
                toast.success('Fornecedor removido com sucesso')
            } else {
                toast.error(response.error || 'Erro ao remover fornecedor')
            }
        } catch {
            toast.error('Erro ao remover fornecedor')
        } finally {
            setDeleteModalId(null)
        }
    }

    if (loading && displayedSuppliers.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando fornecedores...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 md:p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                        <MdLocalShipping className="text-3xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                            Fornecedores
                        </h1>
                        <p className="text-sm md:text-base text-base-content/60 mt-1">
                            {total}{' '}
                            {total === 1
                                ? 'fornecedor cadastrado'
                                : 'fornecedores cadastrados'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {showSearch ? (
                        <form
                            onSubmit={handleSearch}
                            className="flex items-center gap-2 w-full sm:w-auto"
                        >
                            <div className="relative flex-1 sm:flex-initial">
                                <input
                                    type="text"
                                    placeholder="Buscar fornecedores..."
                                    value={searchTerm}
                                    onChange={e =>
                                        setSearchTerm(e.target.value)
                                    }
                                    className="input input-bordered w-full sm:w-72 pl-10"
                                    autoFocus
                                />
                                <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-lg" />
                            </div>
                            <button
                                type="submit"
                                className="btn btn-secondary gap-2"
                            >
                                <MdSearch className="text-lg" />
                                <span className="hidden sm:inline">Buscar</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowSearch(false)
                                    handleClearSearch()
                                }}
                                className="btn btn-ghost gap-2"
                            >
                                <MdClose className="text-lg" />
                                <span className="hidden sm:inline">
                                    Cancelar
                                </span>
                            </button>
                        </form>
                    ) : (
                        <>
                            <button
                                onClick={() => setShowSearch(true)}
                                className="btn btn-ghost gap-2"
                            >
                                <MdSearch className="text-lg" />
                                <span className="hidden sm:inline">Buscar</span>
                            </button>
                            {canCreate && (
                                <button
                                    onClick={() =>
                                        navigate('/person/suppliers/new')
                                    }
                                    className="btn btn-secondary gap-2 shadow-lg shadow-secondary/20"
                                >
                                    <MdAdd className="text-lg" />
                                    <span className="hidden sm:inline">
                                        Novo Fornecedor
                                    </span>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Filtro por tipo */}
            {supplierTypes.length > 0 && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                    <div className="flex items-center gap-3">
                        <MdFilterList className="text-lg text-base-content/60" />
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-base-content/60">
                                Tipo de Fornecedor
                            </label>
                            <select
                                className="select select-bordered select-sm w-52"
                                value={typeFilter || ''}
                                onChange={e =>
                                    setTypeFilter(
                                        e.target.value
                                            ? Number(e.target.value)
                                            : null
                                    )
                                }
                            >
                                <option value="">Todos os tipos</option>
                                {supplierTypes.map(t => (
                                    <option key={t.id} value={t.id}>
                                        {t.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {typeFilter && (
                            <button
                                type="button"
                                onClick={() => setTypeFilter(null)}
                                className="btn btn-ghost btn-sm text-error"
                            >
                                Limpar
                            </button>
                        )}
                    </div>
                </div>
            )}

            {displayedSuppliers.length === 0 && !loading ? (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-2xl border border-base-300/50 shadow-lg p-16 text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-secondary/10 to-secondary/5 flex items-center justify-center">
                        <MdLocalShipping className="text-5xl text-secondary/40" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-base-content mb-3">
                        Nenhum fornecedor encontrado
                    </h3>
                    <p className="text-base-content/60 mb-8 max-w-md mx-auto">
                        {searchTerm
                            ? 'Tente buscar com outros termos ou limpe a busca para ver todos os fornecedores.'
                            : 'Comece adicionando seu primeiro fornecedor ao sistema.'}
                    </p>
                    {!searchTerm && canCreate && (
                        <button
                            onClick={() => navigate('/person/suppliers/new')}
                            className="btn btn-secondary btn-lg gap-2 shadow-lg shadow-secondary/20"
                        >
                            <MdAdd className="text-xl" />
                            Adicionar Fornecedor
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                        {displayedSuppliers.map(supplier => (
                            <div
                                key={supplier.id}
                                onClick={() =>
                                    navigate(`/person/suppliers/${supplier.id}`)
                                }
                                className="group bg-gradient-to-br from-base-100 to-base-200/20 rounded-2xl border border-base-300/50 shadow-sm hover:shadow-xl hover:border-secondary/30 transition-all duration-300 cursor-pointer overflow-hidden relative"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 to-secondary/0 group-hover:from-secondary/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />

                                <div className="relative p-6">
                                    {/* Header com Avatar e Nome */}
                                    <div className="flex items-start gap-4 mb-5">
                                        <div
                                            className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${getAvatarColor(supplier.name)} flex items-center justify-center shadow-lg flex-shrink-0`}
                                        >
                                            <span className="text-2xl font-bold text-white">
                                                {getInitials(supplier.name)}
                                            </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="text-lg font-bold text-base-content group-hover:text-secondary transition-colors line-clamp-1">
                                                    {supplier.name}
                                                </h3>
                                                <span
                                                    className={`badge badge-sm ${
                                                        supplier.active
                                                            ? 'badge-success'
                                                            : 'badge-error'
                                                    }`}
                                                >
                                                    {supplier.active
                                                        ? 'Ativo'
                                                        : 'Inativo'}
                                                </span>
                                                {supplier.type && (
                                                    <span className="badge badge-sm badge-outline badge-secondary">
                                                        {supplier.type.name}
                                                    </span>
                                                )}
                                            </div>
                                            {supplier.tradingName && (
                                                <p className="text-sm text-base-content/60 mb-1 line-clamp-1">
                                                    {supplier.tradingName}
                                                </p>
                                            )}
                                            {supplier.cnpj && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/70">
                                                    <MdBusiness className="text-base text-secondary/60 flex-shrink-0" />
                                                    <span className="font-mono text-xs">
                                                        {formatCnpj(
                                                            supplier.cnpj
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-shrink-0">
                                            <div className="w-8 h-8 rounded-lg bg-base-200/50 group-hover:bg-secondary/10 flex items-center justify-center transition-colors">
                                                <MdArrowForward className="text-base-content/40 group-hover:text-secondary transition-colors" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contato */}
                                    <div className="space-y-1.5 mb-4">
                                        {supplier.email && (
                                            <div className="flex items-center gap-2 text-sm text-base-content/70">
                                                <MdEmail className="text-base text-secondary/60 flex-shrink-0" />
                                                <span className="truncate">
                                                    {supplier.email}
                                                </span>
                                            </div>
                                        )}
                                        {supplier.phone && (
                                            <div className="flex items-center gap-2 text-sm text-base-content/70">
                                                <MdPhone className="text-base text-success/60 flex-shrink-0" />
                                                <span>{supplier.phone}</span>
                                            </div>
                                        )}
                                        {supplier.website && (
                                            <div className="flex items-center gap-2 text-sm text-base-content/70">
                                                <MdLanguage className="text-base text-info/60 flex-shrink-0" />
                                                <span className="truncate">
                                                    {supplier.website}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Localização */}
                                    {(supplier.city || supplier.state) && (
                                        <div className="flex items-center gap-2 text-sm text-base-content/60 mb-4 px-3 py-2 bg-base-200/30 rounded-lg">
                                            <MdLocationOn className="text-base text-info/60 flex-shrink-0" />
                                            <span className="truncate">
                                                {[supplier.city, supplier.state]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </span>
                                        </div>
                                    )}

                                    {/* Footer */}
                                    <div className="pt-4 border-t border-base-300/50">
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-xs gap-1"
                                                    onClick={e =>
                                                        handleToggleActive(
                                                            e,
                                                            supplier.id
                                                        )
                                                    }
                                                    title={
                                                        supplier.active
                                                            ? 'Desativar'
                                                            : 'Ativar'
                                                    }
                                                >
                                                    {supplier.active ? (
                                                        <MdToggleOn className="text-lg text-success" />
                                                    ) : (
                                                        <MdToggleOff className="text-lg text-base-content/40" />
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-xs"
                                                    onClick={e => {
                                                        e.stopPropagation()
                                                        navigate(
                                                            `/person/suppliers/edit/${supplier.id}`
                                                        )
                                                    }}
                                                    title="Editar"
                                                >
                                                    <MdEdit className="text-base" />
                                                </button>
                                                {canDelete && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-xs text-error"
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            setDeleteModalId(
                                                                supplier.id
                                                            )
                                                        }}
                                                        title="Remover"
                                                    >
                                                        <MdDelete className="text-base" />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="text-xs text-base-content/50 text-right">
                                                {format(
                                                    new Date(
                                                        supplier.createdAt
                                                    ),
                                                    'dd/MM/yyyy',
                                                    { locale: ptBR }
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="fornecedor"
                        />
                    )}
                </>
            )}

            {/* Modal de confirmação de exclusão */}
            {deleteModalId && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">
                            Confirmar Exclusão
                        </h3>
                        <p className="py-4 text-base-content/70">
                            Tem certeza que deseja remover este fornecedor? Esta
                            ação não pode ser desfeita.
                        </p>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => setDeleteModalId(null)}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-error"
                                onClick={handleDelete}
                            >
                                Remover
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setDeleteModalId(null)}
                    />
                </div>
            )}
        </div>
    )
}
